import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { from, of, Subscription } from 'rxjs';
import { defer, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NotificationService } from 'src/app/modules/general/services/notification.service';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';

@Component({
  selector: 'app-camera-photo',
  templateUrl: './camera-photo.component.html',
  styleUrls: ['./camera-photo.component.css']
})
export class CameraPhotoComponent implements AfterViewInit, OnInit, OnDestroy {

  @Input()
  WIDTH = 640;

  @Input()
  HEIGHT = 480;

  @ViewChild('video')
  public video: ElementRef;

  @ViewChild('canvas')
  public canvas: ElementRef;

  @Input()
  photos: string[] = [];

  @Input()
  isMulti = true;

  isCaptured: boolean;

  frontCamera: boolean;

  private _mediaStream: any;

  private _setupDeviceSubscription: Subscription;

  constructor(
    private notificationService: NotificationService,
    private loadingSpinnerService: LoadingSpinnerService,
  ) { }

  ngAfterViewInit(): void {
    this._setupDeviceSubscription = this.setupDevices().subscribe();
  }

  ngOnInit(): void {
    if (this.photos == null) {
      this.photos = [];
    }
  }

  ngOnDestroy(): void {
    this.notificationService.hide(CameraPhotoComponent.name);
    this._unsubscribeSetupDeviceSubscription();
    this.disableStream();
  }

  private _unsubscribeSetupDeviceSubscription(): void {
    if (this._setupDeviceSubscription) {
      this._setupDeviceSubscription.unsubscribe();
      this._setupDeviceSubscription = null;
    }
  }

  disableStream(): void {
    if (this._mediaStream) {
      for (const mediaTrack of this._mediaStream.getVideoTracks()) {
        if (mediaTrack) {
          mediaTrack.stop();
        }
      }
      this._mediaStream = null;
    }
  }

  setupDevices(): Observable<boolean> {
    return defer(() => {
      this.disableStream();
      if (!navigator || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return of(false);
      }
      this.loadingSpinnerService.show();
      return from(navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: (this.frontCamera ? 'user' : 'environment'),
          width: { ideal: 4096 },
          height: { ideal: 2160 },
        }
      })).pipe(
        map(mediaStream => {
          this._mediaStream = mediaStream;
          if (this._mediaStream) {

            const streamSettings = this._mediaStream.getVideoTracks()[0].getSettings();

            // actual width & height of the camera video
            const streamWidth = streamSettings.width;
            const streamHeight = streamSettings.height;

            this.WIDTH = streamWidth;
            this.HEIGHT = streamHeight;

            this.video.nativeElement.srcObject = this._mediaStream;
            this.video.nativeElement.play();
            this.notificationService.hide(CameraPhotoComponent.name);
            this.loadingSpinnerService.hide();
            return true;
          } else {
            this.notificationService.show(CameraPhotoComponent.name, 'Camera not found');
            this.loadingSpinnerService.hide();
            return false;
          }
        }),
        catchError(error => {
          this.notificationService.show(CameraPhotoComponent.name, error);
          this.loadingSpinnerService.hide();
          return of(false);
        })
      );
    });
  }

  capture(): void {
    this.drawImageToCanvas(this.video.nativeElement);

    if (!this.isMulti) {
      this.photos.length = 0;
    }
    this.photos.push(this.canvas.nativeElement.toDataURL('image/jpeg', 1.0));

    this.isCaptured = true;
  }

  reCapture(): void {
    this.photos.length = 0;
    this.isCaptured = false;
  }

  drawImageToCanvas(image: any): void {
    this.canvas.nativeElement
      .getContext('2d')
      .drawImage(image, 0, 0, this.WIDTH, this.HEIGHT);
  }

  onChangeCamera(): void {
    this.frontCamera = !this.frontCamera;
    this._unsubscribeSetupDeviceSubscription();
    this.setupDevices().subscribe();
  }
}
